import React from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';

/**
 * Flight-risk drill-down — composite score breakdown + 12-month sales trend.
 *
 * Reads context with kind='flight_risk_breakdown':
 *   composite_score, ocr, trend, weights, scores,
 *   annual_rent, annual_charges, annual_costs, total_12m_sales,
 *   lease_start, lease_end, lease_pct_elapsed,
 *   monthly_sales: [{month, value}, ...]
 */

function pct(v) {
    if (v == null) return '--';
    return `${(v * 100).toFixed(1)}%`;
}

function currency(v) {
    if (v == null) return '--';
    return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function tierFromScore(score) {
    if (score == null) return { label: 'unknown', color: 'var(--v3-text-muted)' };
    if (score >= 0.7) return { label: 'DANGER', color: 'var(--v3-red)' };
    if (score >= 0.4) return { label: 'WATCH', color: 'var(--v3-amber)' };
    return { label: 'HEALTHY', color: 'var(--v3-green)' };
}

function severityWord(score, t) {
    if (score == null) return '--';
    if (score >= 0.7) return t('Critical');
    if (score >= 0.4) return t('Watch');
    if (score >= 0.15) return t('Mild');
    return t('Healthy');
}

function monthsUntil(dateStr) {
    if (!dateStr) return null;
    const end = new Date(dateStr);
    if (isNaN(end)) return null;
    const diffMs = end.getTime() - Date.now();
    return Math.round(diffMs / (1000 * 60 * 60 * 24 * 30));
}

function capFirst(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function summarySentence(ctx, tenantName, t) {
    const { annual_costs, total_12m_sales, trend, lease_end } = ctx || {};
    if (annual_costs == null || total_12m_sales == null) return null;
    const tenant = tenantName || t('Tenant');
    const parts = [t('{{tenant}} spends {{cost}}/yr but earns {{sales}}', {
        tenant, cost: currency(annual_costs), sales: currency(total_12m_sales),
    })];
    if (trend != null) {
        if (trend < -0.02) parts.push(t('Sales falling'));
        else if (trend > 0.02) parts.push(t('Sales growing'));
        else parts.push(t('Sales flat'));
    }
    const months = monthsUntil(lease_end);
    if (months != null) {
        if (months <= 0) parts.push(t('Lease expired'));
        else if (months <= 6) parts.push(t('Lease ends in {{n}} months', { n: months }));
    }
    return parts.map(capFirst).join('. ') + '.';
}

function ocrTierColor(ocr) {
    if (ocr == null) return 'var(--v3-text-muted)';
    if (ocr >= 0.25) return 'var(--v3-red)';
    if (ocr >= 0.10) return 'var(--v3-amber)';
    return 'var(--v3-green)';
}

function ScoreBar({ label, weight, score, color, t, tooltip }) {
    const weightPct = weight * 100;
    const filled = Math.min(100, (score ?? 0) * 100);
    const word = severityWord(score, t);
    const numericTitle = score != null ? `${score.toFixed(2)} (${(score * weight).toFixed(2)} weighted)` : '';
    return (
        <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                <span title={tooltip} style={{ color: 'var(--v3-text-secondary)', cursor: tooltip ? 'help' : 'default' }}>
                    {label} <span style={{ color: 'var(--v3-text-muted)' }}>({weightPct}%)</span>
                </span>
                <span title={numericTitle} style={{ color, fontWeight: 600 }}>{word}</span>
            </div>
            <div style={{
                height: 6, borderRadius: 3,
                background: 'var(--v3-bg-surface)',
                overflow: 'hidden',
            }}>
                <div style={{
                    width: `${filled}%`, height: '100%', borderRadius: 3,
                    background: color,
                    transition: 'width 0.4s ease',
                }} />
            </div>
        </div>
    );
}

export default function FlightRiskDrilldown({ context, tenantName }) {
    const { t } = useTranslation();
    const {
        composite_score, ocr, trend,
        weights = {}, scores = {},
        annual_rent, annual_charges, annual_costs, total_12m_sales,
        lease_start, lease_end, lease_pct_elapsed,
        monthly_sales = [],
    } = context || {};

    const tier = tierFromScore(composite_score);
    const isDark = document.documentElement.getAttribute('data-theme') !== 'light';

    // ── Monthly sales trend chart ──
    const chartSeries = monthly_sales.length ? [{
        name: t('Sales'),
        data: monthly_sales.map(p => ({ x: p.month, y: Number(p.value) || 0 })),
    }] : [];

    // ApexCharts needs resolved hex values, not CSS vars
    const accentHex = isDark ? '#3B82F6' : '#2563EB';
    const accentDimHex = isDark ? 'rgba(59,130,246,0.35)' : 'rgba(37,99,235,0.30)';
    const gridColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)';
    const labelColor = isDark ? '#64748B' : '#94A3B8';

    const chartOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Inter, system-ui', background: 'transparent' },
        colors: monthly_sales.map((_, i) => i >= monthly_sales.length - 3 ? accentHex : accentDimHex),
        plotOptions: { bar: { borderRadius: 3, columnWidth: '60%', distributed: true } },
        grid: { borderColor: gridColor, strokeDashArray: 4 },
        xaxis: { type: 'category', labels: { style: { colors: labelColor, fontSize: '9px' }, rotate: -45 } },
        yaxis: { labels: {
            style: { colors: labelColor, fontSize: '10px', fontFamily: 'JetBrains Mono, monospace' },
            formatter: (v) => Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v),
        }},
        tooltip: { theme: isDark ? 'dark' : 'light' },
        dataLabels: { enabled: false },
        legend: { show: false },
    };

    // Color thresholds aligned with severityWord(): Critical≥0.7, Watch≥0.4,
    // Mild≥0.15, Healthy<0.15. Mild + Watch share amber so any concern reads
    // as "not green" — the word disambiguates magnitude.
    const barColor = (score) => {
        if (score >= 0.7) return 'var(--v3-red)';
        if (score >= 0.15) return 'var(--v3-amber)';
        return 'var(--v3-green)';
    };

    const trendColor = (trend ?? 0) < -0.05 ? 'var(--v3-red)' : (trend ?? 0) > 0.05 ? 'var(--v3-green)' : 'var(--v3-amber)';
    const leaseColor = (lease_pct_elapsed ?? 0) > 0.8 ? 'var(--v3-red)' : (lease_pct_elapsed ?? 0) > 0.6 ? 'var(--v3-amber)' : 'var(--v3-green)';

    const plainSummary = summarySentence(context, tenantName, t);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* ── Plain-English summary (layman view) ── */}
            {plainSummary && (
                <div style={{
                    padding: '10px 12px', borderRadius: 6,
                    background: 'var(--v3-bg-surface)',
                    borderLeft: `3px solid ${tier.color}`,
                    fontSize: 13, lineHeight: 1.45,
                    color: 'var(--v3-text-primary)',
                }}>
                    {plainSummary}
                </div>
            )}

            {/* ── Composite score header ── */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderRadius: 8,
                background: 'var(--v3-card-bg)', border: '1px solid var(--v3-border)',
            }}>
                <div style={{
                    width: 48, height: 48, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `2px solid ${tier.color}`,
                    fontFamily: 'JetBrains Mono, monospace', fontSize: 16, fontWeight: 700,
                    color: tier.color,
                }}>
                    {composite_score != null ? composite_score.toFixed(2) : '--'}
                </div>
                <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: tier.color }}>{t(tier.label)}</div>
                    <div style={{ fontSize: 11, color: 'var(--v3-text-muted)' }}>{t('Composite flight-risk score')}</div>
                </div>
            </div>

            {/* ── Score breakdown bars ── */}
            <div>
                <h5 style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--v3-text-muted)', margin: '0 0 10px' }}>
                    {t('Score breakdown')}
                </h5>
                <ScoreBar
                    label={t('Rent-to-sales ratio')} weight={weights.ocr ?? 0.4}
                    score={scores.ocr} color={barColor(scores.ocr ?? 0)} t={t}
                    tooltip={t('Rent + charges as % of sales. Healthy: under 20%. Yours: {{v}}.', { v: pct(ocr) })}
                />
                <ScoreBar
                    label={t('Sales direction')} weight={weights.trend ?? 0.3}
                    score={scores.trend} color={barColor(scores.trend ?? 0)} t={t}
                    tooltip={t('Last 3 months vs 6-month run rate. Healthy: 0% or up. Yours: {{v}}.', {
                        v: trend != null ? `${trend > 0 ? '+' : ''}${(trend * 100).toFixed(1)}%` : '--',
                    })}
                />
                <ScoreBar
                    label={t('Lease ending soon')} weight={weights.proximity ?? 0.2}
                    score={scores.proximity} color={barColor(scores.proximity ?? 0)} t={t}
                    tooltip={t('% of lease term elapsed. Healthy: under 60%. Yours: {{v}}.', { v: pct(lease_pct_elapsed) })}
                />
                <ScoreBar
                    label={t('Dead trading days')} weight={weights.streak ?? 0.1}
                    score={scores.streak} color={barColor(scores.streak ?? 0)} t={t}
                    tooltip={t('Longest recent streak of zero-sales days. Healthy: 0 days.')}
                />
            </div>

            {/* ── Key metrics grid ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={metricBoxStyle}>
                    <div style={metricLabelStyle}>{t('OCR')}</div>
                    <div style={{ ...metricValueStyle, color: ocrTierColor(ocr) }}>{pct(ocr)}</div>
                    <div style={metricHintStyle}>{t('Costs / Sales')}</div>
                </div>
                <div style={metricBoxStyle}>
                    <div style={metricLabelStyle}>{t('Sales Trend')}</div>
                    <div style={{ ...metricValueStyle, color: trendColor }}>
                        {trend != null ? `${trend > 0 ? '+' : ''}${(trend * 100).toFixed(1)}%` : '--'}
                    </div>
                    <div style={metricHintStyle}>{t('3m vs 6m run rate')}</div>
                </div>
                <div style={metricBoxStyle}>
                    <div style={metricLabelStyle}>{t('Annual Costs')}</div>
                    <div style={metricValueStyle}>{currency(annual_costs)}</div>
                    <div style={metricHintStyle}>{t('Rent {{rent}} + Charges {{charges}}', { rent: currency(annual_rent), charges: currency(annual_charges) })}</div>
                </div>
                <div style={metricBoxStyle}>
                    <div style={metricLabelStyle}>{t('12m Sales')}</div>
                    <div style={metricValueStyle}>{currency(total_12m_sales)}</div>
                </div>
            </div>

            {/* ── Lease info ── */}
            {lease_start && lease_end && (
                <div style={metricBoxStyle}>
                    <div style={metricLabelStyle}>{t('Lease')}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                            flex: 1, height: 6, borderRadius: 3,
                            background: 'var(--v3-bg-surface)',
                            overflow: 'hidden',
                        }}>
                            <div style={{
                                width: `${Math.min(100, (lease_pct_elapsed ?? 0) * 100)}%`,
                                height: '100%', borderRadius: 3,
                                background: leaseColor,
                            }} />
                        </div>
                        <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: 'var(--v3-text-secondary)' }}>
                            {pct(lease_pct_elapsed)}
                        </span>
                    </div>
                    <div style={metricHintStyle}>{t('{{start}} to {{end}}', { start: lease_start, end: lease_end })}</div>
                </div>
            )}

            {/* ── 12-month sales trend chart ── */}
            {chartSeries.length > 0 && (
                <div>
                    <h5 style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--v3-text-muted)', margin: '0 0 6px' }}>
                        {t('Monthly sales (12 months)')}
                    </h5>
                    <div style={{ height: 180 }}>
                        <Chart type="bar" options={chartOptions} series={chartSeries} height={180} />
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--v3-text-muted)', textAlign: 'center', marginTop: 2 }}>
                        {t('Last 3 months highlighted — used for trend calculation')}
                    </div>
                </div>
            )}
        </div>
    );
}

const metricBoxStyle = {
    padding: '8px 10px', borderRadius: 6,
    background: 'var(--v3-card-bg)', border: '1px solid var(--v3-border)',
};
const metricLabelStyle = {
    fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em',
    color: 'var(--v3-text-muted)', marginBottom: 2,
};
const metricValueStyle = {
    fontSize: 16, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace',
    color: 'var(--v3-text-primary)',
};
const metricHintStyle = {
    fontSize: 10, color: 'var(--v3-text-muted)', marginTop: 2,
};
